import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

export const AGOL_SEARCH_BASE_URL =
  "https://placepoint.maps.arcgis.com/home/content.html?searchTerm="

/**
 * Operation name of layer.query.gql (the layer tables); refetched by every
 * mutation that adds/removes runtime rows or flips fields the tables show.
 */
export const LAYER_QUERY_NAME = "LayerQuery"

/** Operation name of view/layer.view.query.gql (the open layer sheet). */
export const LAYER_VIEW_QUERY_NAME = "LayerViewQuery"

/**
 * Operation name of drift/layer.drift.query.gql. Refetched after a layer
 * save: severity and remediation are computed from the layer's own saved
 * documents, so editing them is what resolves the drift.
 */
export const LAYER_DRIFT_QUERY_NAME = "LayerDriftQuery"

/**
 * Every gis.Layers feature code starts with this. Used to recognize a layer
 * code on surfaces that only see bare feature codes (the Translation tab's
 * Review pending list) so they can hot-link into the layer sheet.
 */
export const MAP_LAYER_FEATURE_CODE_PREFIX = "MapLayer_"

/** The layers page href that opens a layer's view sheet. */
export function getLayerSheetHref(featureCode: string): string {
  return `/layers?v=layer:${encodeURIComponent(featureCode)}`
}

/**
 * Source key the uptime/drift monitor uses for individual map layers
 * (targets are keyed (Source, Identifier) where Identifier is the layer's
 * FeatureCode). Shared by the uptime and drift lookup providers so both
 * always read from the bucket the etl writes into.
 */
export const MAP_LAYER_MONITOR_SOURCE = "maplayer"

/**
 * `id` of the Layers page's tab strip. Unchanged from when the page held
 * layers alone, so existing `?layers-tab=…` links still land.
 */
export const LAYER_PAGE_TABS_ID = "layers"

/**
 * The page's tabs: the catalogs an operator edits, then the review states a
 * map layer moves through. Values are the URL's, so they are frozen.
 */
export const LAYER_PAGE_TAB = {
  layers: "layers",
  basemaps: "basemaps",
  navLayers: "navlayers",
  categories: "categories",
  review: "review",
  rejected: "rejected",
} as const

export type LayerPageTab = (typeof LAYER_PAGE_TAB)[keyof typeof LAYER_PAGE_TAB]

/** The tab the page opens on when the URL says nothing. */
export const LAYER_PAGE_DEFAULT_TAB: LayerPageTab = LAYER_PAGE_TAB.layers

/** Narrows a raw `?layers-tab=` value; anything unknown reads as default. */
export function toLayerPageTab(value: string): LayerPageTab {
  const isKnown = Object.values<string>(LAYER_PAGE_TAB).includes(value)
  return isKnown ? (value as LayerPageTab) : LAYER_PAGE_DEFAULT_TAB
}

/**
 * `id` of the layer sheet's tab strip. The tab in view rides in the
 * `<id>-tab` search param (see components/tabs useTabParam), which is what
 * lets the Drift tab hand an operator to the document that needs the edit
 * with a plain link rather than cross-tab state.
 */
export const LAYER_VIEW_TABS_ID = "layer-view-tabs"

/** The tab the sheet opens on when the URL says nothing. */
export const LAYER_VIEW_DEFAULT_TAB = "configuration"

/** Where drift is resolved — the uptime sheet only summarises it. */
export const LAYER_VIEW_DRIFT_TAB = "drift"

/**
 * The five JSON documents a layer is made of, in the order the sheet
 * shows them. Drift is scanned against all five, so anything that walks
 * documents — the tab strip, the drift hand-off links — walks this.
 */
export const LAYER_DOCUMENT_FIELDS: LayerEditorField[] = [
  "Configuration",
  "Filters",
  "Interactions",
  "Export",
  "Table",
]

/**
 * A document's tab value is its lowercased name. One function so the tab
 * strip and the links that address it can't disagree.
 */
export function getLayerDocumentTabValue(field: string): string {
  return field.toLowerCase()
}
